import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import axios from "axios";
import { ArrowRight, ShieldCheck, Clock, MapPin, CalendarCheck, CreditCard, Truck, Star } from "lucide-react";
import { SERVICE_META } from "@/data/services";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const MARQUEE = ["5-Star Rated", "Mobil 1", "Bosch", "NAPA Parts", "Same-Day Service", "Fully Insured", "OEM Filters", "Wagner Parts"];

const STEPS = [
  { icon: CalendarCheck, title: "Book Online", text: "Pick your service, date and a time slot that works for you." },
  { icon: CreditCard, title: "Secure Deposit", text: "Pay a small deposit to lock in your slot — the rest after the job." },
  { icon: Truck, title: "We Come To You", text: "Our certified mechanic arrives at your driveway, fully equipped." },
];

const TESTIMONIALS = [
  { name: "Marcus T.", quote: "ESMS is great. I booked a brake service on Monday and it was done in my driveway by Tuesday. No tow, no waiting room.", car: "'19 F-150" },
  { name: "Priya R.", quote: "There's a small deposit, but the price is great. The inspection report was incredibly detailed.", car: "'21 CR-V" },
  { name: "Devon K.", quote: "Battery died at home. ESMS showed up same day and had me running in 30 minutes.", car: "'17 Civic" },
];

export default function Landing() {
  const [services, setServices] = useState([]);
  const navigate = useNavigate();

  useEffect(() => {
    axios.get(`${API}/services`).then((r) => setServices(r.data)).catch(() => {});
  }, []);

  return (
    <main data-testid="landing-page">
      {/* HERO */}
      <section className="relative min-h-screen flex items-center pt-20 overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://images.pexels.com/photos/8478259/pexels-photo-8478259.jpeg"
            alt="Mobile mechanic working on engine"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-black via-black/85 to-black/40" />
          <div className="absolute inset-0 grain opacity-20 mix-blend-overlay" />
        </div>

        <div className="relative max-w-7xl mx-auto px-5 sm:px-8 w-full">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7 }}
            className="max-w-2xl"
          >
            <div data-testid="promo-banner" className="flex w-fit items-center gap-1.5 sm:gap-2 whitespace-nowrap max-w-full bg-yellow-400 text-black rounded-sm px-2.5 sm:px-4 py-2 mb-6">
              <span className="text-[11px] sm:text-sm font-extrabold uppercase tracking-tight sm:tracking-wide">$20 off first oil change</span>
              <span className="text-black/60">·</span>
              <span data-testid="promo-code" className="inline-flex items-center rounded-sm bg-black text-yellow-400 font-mono font-bold text-[11px] sm:text-sm px-1.5 sm:px-2 py-0.5 tracking-widest">EASY20</span>
            </div>
            <span className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-[0.25em] text-yellow-400 mb-6">
              <span className="w-8 h-px bg-yellow-400" /> Easy Stop Mobile Service
            </span>
            <h1 className="font-heading font-extrabold text-4xl sm:text-5xl lg:text-7xl leading-[0.95] tracking-tighter uppercase text-white">
              The Garage <br />
              <span className="text-yellow-400">Comes To You.</span>
            </h1>
            <p className="mt-6 text-lg text-neutral-300 max-w-lg leading-relaxed">
              Certified Charlotte mechanics, fair pricing, and no towing. We come right to your driveway
              for oil changes, brakes, inspections and more. We make it an Easy Stop.
            </p>
            <div className="mt-9 flex flex-wrap items-center gap-4">
              <button
                data-testid="hero-book-button"
                onClick={() => navigate("/book")}
                className="group inline-flex items-center gap-2 bg-yellow-400 text-black font-bold uppercase tracking-wide px-8 py-4 rounded-sm hover:bg-yellow-500 transition-colors"
              >
                Book a Service
                <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
              </button>
              <Link
                to="/services"
                data-testid="hero-services-link"
                className="inline-flex items-center gap-2 border border-neutral-700 text-white font-bold uppercase tracking-wide px-8 py-4 rounded-sm hover:border-yellow-400 transition-colors"
              >
                View Services
              </Link>
            </div>

            <div className="mt-12 flex flex-wrap gap-x-8 gap-y-3 text-sm text-neutral-300">
              <span className="flex items-center gap-2"><ShieldCheck className="w-4 h-4 text-yellow-400" /> Fully Insured</span>
              <span className="flex items-center gap-2"><Clock className="w-4 h-4 text-yellow-400" /> Same-Day Slots</span>
              <span className="flex items-center gap-2"><MapPin className="w-4 h-4 text-yellow-400" /> We Travel To You</span>
            </div>
          </motion.div>
        </div>
      </section>

      {/* MARQUEE */}
      <section className="border-y border-neutral-800 bg-[#0A0A0A] py-5 overflow-hidden">
        <div className="flex whitespace-nowrap animate-marquee w-max">
          {[...MARQUEE, ...MARQUEE].map((m, i) => (
            <span key={`${m}-${i}`} className="mx-8 text-sm font-bold uppercase tracking-[0.2em] text-neutral-500 flex items-center gap-8">
              {m} <span className="w-1.5 h-1.5 bg-yellow-400 rounded-full" />
            </span>
          ))}
        </div>
      </section>

      {/* SERVICES BENTO */}
      <section className="max-w-7xl mx-auto px-5 sm:px-8 py-24" data-testid="landing-services">
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-12">
          <div>
            <span className="text-xs font-bold uppercase tracking-[0.2em] text-yellow-400">What We Do</span>
            <h2 className="font-heading font-bold text-3xl sm:text-4xl uppercase tracking-tight text-white mt-3">
              Core Services
            </h2>
          </div>
          <Link to="/services" className="text-sm font-bold uppercase tracking-wide text-neutral-300 hover:text-yellow-400 flex items-center gap-2">
            See all details <ArrowRight className="w-4 h-4" />
          </Link>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {services.map((s, i) => {
            const meta = SERVICE_META[s.id] || {};
            const Icon = meta.icon;
            const span = i === 0 ? "md:col-span-2" : "";
            return (
              <motion.div
                key={s.id}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: i * 0.08 }}
                onClick={() => navigate("/services")}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => { if (e.key === "Enter") navigate("/services"); }}
                className={`group relative bg-[#141414] border border-neutral-800 rounded-sm p-7 hover:border-yellow-400 transition-colors overflow-hidden cursor-pointer ${span}`}
                data-testid={`landing-service-${s.id}`}
              >
                <div className="flex items-start justify-between mb-6">
                  {Icon && (
                    <div className="w-12 h-12 bg-neutral-900 border border-neutral-800 rounded-sm flex items-center justify-center group-hover:bg-yellow-400 transition-colors">
                      <Icon className="w-6 h-6 text-yellow-400 group-hover:text-black transition-colors" />
                    </div>
                  )}
                  <span className="font-mono text-xs text-neutral-500">0{i + 1}</span>
                </div>
                <h3 className="font-heading font-bold text-xl text-white mb-2">{s.name}</h3>
                <p className="text-sm text-neutral-400 leading-relaxed mb-5">{s.tagline}</p>
                <div className="flex items-center gap-4 text-sm">
                  <span className="font-mono text-yellow-400 font-semibold">${s.price.toFixed(0)}</span>
                  <span className="text-neutral-500">Deposit ${s.deposit.toFixed(0)}</span>
                </div>
              </motion.div>
            );
          })}
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section className="bg-[#0D0D0D] border-y border-neutral-800 py-24">
        <div className="max-w-7xl mx-auto px-5 sm:px-8">
          <div className="text-center mb-16">
            <span className="text-xs font-bold uppercase tracking-[0.2em] text-yellow-400">Simple Process</span>
            <h2 className="font-heading font-bold text-3xl sm:text-4xl uppercase tracking-tight text-white mt-3">How It Works</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-10">
            {STEPS.map((step, i) => {
              const Icon = step.icon;
              return (
                <motion.div
                  key={step.title}
                  initial={{ opacity: 0, y: 24 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: i * 0.12 }}
                  className="relative text-center md:text-left"
                  data-testid={`step-${i + 1}`}
                >
                  <span className="font-heading font-extrabold text-7xl text-neutral-800 absolute -top-6 right-0 md:left-0 select-none">0{i + 1}</span>
                  <div className="relative w-14 h-14 bg-yellow-400 rounded-sm flex items-center justify-center mb-5 mx-auto md:mx-0">
                    <Icon className="w-7 h-7 text-black" />
                  </div>
                  <h3 className="font-heading font-bold text-xl text-white mb-3">{step.title}</h3>
                  <p className="text-neutral-400 leading-relaxed">{step.text}</p>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* TESTIMONIALS */}
      <section className="max-w-7xl mx-auto px-5 sm:px-8 py-24">
        <div className="mb-14">
          <span className="text-xs font-bold uppercase tracking-[0.2em] text-yellow-400">Driveway Reviews</span>
          <h2 className="font-heading font-bold text-3xl sm:text-4xl uppercase tracking-tight text-white mt-3">Drivers Trust Us</h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {TESTIMONIALS.map((t, i) => (
            <div key={t.name} className="bg-[#141414] border border-neutral-800 rounded-sm p-7" data-testid={`testimonial-${i}`}>
              <div className="flex gap-1 mb-4">
                {[...Array(5)].map((_, j) => <Star key={`${t.name}-star-${j}`} className="w-4 h-4 fill-yellow-400 text-yellow-400" />)}
              </div>
              <p className="text-neutral-200 leading-relaxed mb-6">"{t.quote}"</p>
              <div className="flex items-center justify-between">
                <span className="font-bold text-white">{t.name}</span>
                <span className="font-mono text-xs text-neutral-500">{t.car}</span>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="max-w-7xl mx-auto px-5 sm:px-8 pb-8">
        <div className="relative bg-yellow-400 rounded-sm px-8 py-16 sm:px-16 overflow-hidden">
          <div className="relative z-10 max-w-xl">
            <h2 className="font-heading font-extrabold text-3xl sm:text-5xl uppercase tracking-tighter text-black leading-none">
              Ready when you are.
            </h2>
            <p className="text-black/80 mt-4 text-lg font-medium">
              Reserve your slot in under two minutes. Deposit secured, mechanic dispatched.
            </p>
            <button
              data-testid="cta-book-button"
              onClick={() => navigate("/book")}
              className="mt-8 inline-flex items-center gap-2 bg-black text-yellow-400 font-bold uppercase tracking-wide px-8 py-4 rounded-sm hover:bg-neutral-900 transition-colors"
            >
              Book Now <ArrowRight className="w-5 h-5" />
            </button>
          </div>
          <Truck className="absolute -right-6 -bottom-6 w-64 h-64 text-black/10" strokeWidth={1} />
        </div>
      </section>
    </main>
  );
}

